import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../api/client';
import { getThemeColors } from '../utils/themeStyles';
import { COUNTRIES } from '../config/countries';
import ErrorRetry from '../components/ErrorRetry';

// Profil EN BASE (Student) — source de vérité des informations personnelles.
interface ProfileResponse {
  studentId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null; // code ISO (ou ancienne valeur libre)
  city?: string | null;
  age?: number | null;
}

export default function MyInformationScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const { user, getAccessToken } = useAuth();
  const colors = getThemeColors(isDarkTheme);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [country, setCountry] = useState(''); // code ISO
  const [countryQuery, setCountryQuery] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);

  const countryLabel = useMemo(() => {
    if (!country) return '';
    return COUNTRIES.find(c => c.code === country)?.name ?? country;
  }, [country]);
  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
  }, [countryQuery]);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setError(false); setLoading(false); return; }
    setLoading(true);
    try {
      const p = await apiFetch<ProfileResponse>('/me/profile', { token, auth: true });
      setFirstName(p?.firstName ?? '');
      setLastName(p?.lastName ?? '');
      setEmail(p?.email ?? user?.email ?? '');
      setPhone(p?.phone ?? '');
      setAge(p?.age != null ? String(p.age) : '');
      setCountry(p?.country ?? '');
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    const token = getAccessToken();
    if (!token) return;
    setSaving(true);
    setSaved(false);
    try {
      const ageNum = age.trim() ? parseInt(age, 10) : null;
      await apiFetch('/me/profile', {
        method: 'POST',
        token,
        auth: true,
        body: {
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          phone: phone.trim() || null,
          country: country || null,
          age: Number.isNaN(ageNum as number) ? null : ageNum,
        },
      });
      setSaved(true);
    } catch (_) {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const input = (
    label: string,
    value: string,
    onChange: (t: string) => void,
    extra?: object,
  ) => (
    <View style={styles.field}>
      <Text style={[styles.label, { color: colors.textTertiary }]}>{label}</Text>
      <TextInput
        style={[styles.input, { color: colors.textPrimary, borderBottomColor: colors.border }]}
        value={value}
        onChangeText={t => { onChange(t); setSaved(false); }}
        placeholderTextColor={colors.inputPlaceholder}
        editable={!saving}
        {...extra}
      />
    </View>
  );

  const readonly = (icon: string, label: string, value: string) => (
    <View style={styles.field}>
      <Text style={[styles.label, { color: colors.textTertiary }]}>{label}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Ionicons name={icon as any} size={16} color={colors.iconSecondary} style={{ marginRight: 8 }} />
        <Text style={[styles.value, { color: colors.textSecondary }]} selectable>{value || '—'}</Text>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Account' as never))} style={styles.backButton}>
          <Ionicons name="arrow-back" size={22} color={colors.iconPrimary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>My Information</Text>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} contentContainerStyle={{ padding: 16 }} keyboardShouldPersistTaps="handled">
        {error && loading ? (
          <ErrorRetry onRetry={() => void load()} retrying={loading} message="Impossible de charger vos informations." />
        ) : loading ? (
          <View style={styles.loaderBox}><ActivityIndicator color={colors.primary} /></View>
        ) : (
          <View style={[styles.card, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.cardHeader, { color: colors.textSecondary }]}>Détails personnels</Text>

            {input('Prénom', firstName, setFirstName, { autoCapitalize: 'words', placeholder: 'Prénom' })}
            {input('Nom', lastName, setLastName, { autoCapitalize: 'words', placeholder: 'Nom' })}
            {input('Téléphone', phone, setPhone, { keyboardType: 'phone-pad', placeholder: '—' })}
            {input('Âge', age, setAge, { keyboardType: 'number-pad', maxLength: 3, placeholder: '—' })}

            {/* Pays — sélecteur filtrable */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: colors.textTertiary }]}>Pays</Text>
              <TouchableOpacity
                style={[styles.input, styles.selectRow, { borderBottomColor: colors.border }]}
                onPress={() => setCountryOpen(o => !o)}
                disabled={saving}
                activeOpacity={0.7}
              >
                <Text style={{ color: countryLabel ? colors.textPrimary : colors.inputPlaceholder }}>
                  {countryLabel || 'Sélectionner un pays'}
                </Text>
                <Text style={{ color: colors.textSecondary }}>{countryOpen ? '▲' : '▼'}</Text>
              </TouchableOpacity>
              {countryOpen && (
                <View style={[styles.dropdown, { borderColor: colors.border, backgroundColor: colors.background }]}>
                  <TextInput
                    style={[styles.input, { color: colors.textPrimary, borderBottomColor: colors.border }]}
                    placeholder="Rechercher…"
                    placeholderTextColor={colors.inputPlaceholder}
                    value={countryQuery}
                    onChangeText={setCountryQuery}
                    autoCapitalize="none"
                  />
                  <ScrollView style={styles.dropdownList} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
                    {filteredCountries.map(c => (
                      <TouchableOpacity
                        key={c.code}
                        style={styles.option}
                        onPress={() => { setCountry(c.code); setCountryOpen(false); setCountryQuery(''); setSaved(false); }}
                      >
                        <Text style={{ color: colors.textPrimary }}>{c.name}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>

            {/* Champs non modifiables (issus de l'identité Entra) */}
            {readonly('mail-outline', 'Email', email)}
            {readonly('finger-print-outline', 'Identifiant', user?.id || '—')}

            <TouchableOpacity
              style={[styles.saveBtn, { backgroundColor: colors.primary }, saving && { opacity: 0.6 }]}
              onPress={save}
              disabled={saving}
              activeOpacity={0.85}
            >
              {saving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.saveText}>Enregistrer</Text>}
            </TouchableOpacity>
            {saved ? <Text style={[styles.savedMsg, { color: colors.primary }]}>✓ Informations enregistrées</Text> : null}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', padding: 16, borderBottomWidth: 1 },
  backButton: { padding: 6, marginRight: 8 },
  headerTitle: { fontSize: 20, fontWeight: 'bold' },
  content: { flex: 1 },
  loaderBox: { paddingVertical: 48, alignItems: 'center' },
  card: { borderRadius: 12, padding: 16 },
  cardHeader: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6, paddingBottom: 12 },
  field: { marginBottom: 16 },
  label: { fontSize: 12, marginBottom: 4 },
  input: { fontSize: 15, fontWeight: '600', paddingVertical: 8, borderBottomWidth: 1 },
  selectRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  dropdown: { borderWidth: 1, borderRadius: 8, marginTop: 6, padding: 8 },
  dropdownList: { maxHeight: 200 },
  option: { paddingVertical: 10, paddingHorizontal: 4 },
  value: { fontSize: 15, fontWeight: '600' },
  saveBtn: { marginTop: 8, borderRadius: 8, paddingVertical: 14, alignItems: 'center' },
  saveText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  savedMsg: { textAlign: 'center', marginTop: 10, fontSize: 13, fontWeight: '600' },
});
